import React, { useContext, useEffect, useState } from 'react';
import { useApi } from 'src/hooks/useApi';
import { useAuth } from './AuthProvider';

export interface ContentPolicyContextType {
    hasAcceptedPolicy: boolean;
    isLoading: boolean;
    acceptPolicy: () => Promise<void>;
}

export const ContentPolicyContext =
    React.createContext<ContentPolicyContextType>({
        hasAcceptedPolicy: true,
        isLoading: true,
        acceptPolicy: async () => {},
    });

export const useContentPolicy = () => useContext(ContentPolicyContext);

interface ContentPolicyProviderProps {
    children: React.ReactNode;
}

export const ContentPolicyProvider = ({
    children,
}: ContentPolicyProviderProps) => {
    const { user } = useAuth();
    const [hasAcceptedPolicy, setHasAcceptedPolicy] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const { makeRequest: getAcceptance } = useApi({
        url: 'user-profile/content-policy-acceptance',
        method: 'GET',
    });

    const { makeRequest: postAcceptance } = useApi({
        url: 'user-profile/content-policy-acceptance',
        method: 'POST',
    });

    useEffect(() => {
        const checkPolicyAcceptance = async () => {
            if (!user) {
                setIsLoading(false);
                return;
            }

            try {
                const response = await getAcceptance();
                const accepted = response?.data?.hasAccepted ?? false;
                setHasAcceptedPolicy(accepted);
            } catch (error) {
                console.error(
                    'Error checking content policy acceptance:',
                    error
                );
                setHasAcceptedPolicy(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkPolicyAcceptance();
    }, [user]);

    const acceptPolicy = async () => {
        try {
            await postAcceptance({
                data: { acceptedDate: new Date().toISOString() },
            });
            setHasAcceptedPolicy(true);
        } catch (error) {
            console.error('Error accepting content policy:', error);
            throw error;
        }
    };

    const value: ContentPolicyContextType = {
        hasAcceptedPolicy,
        isLoading,
        acceptPolicy,
    };

    return (
        <ContentPolicyContext.Provider value={value}>
            {children}
        </ContentPolicyContext.Provider>
    );
};
